import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { HouseholdSchedule } from '../../../../common/src/model.ts';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import * as server_api from '../../../../common/src/server_api.ts';
import { LocalStorageKey } from '../../data/client_local_storage.ts';
import { synchronizeHouseholdWithServer, useHousehold } from '../../data/use_household.tsx';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { Button, LeftAlignedButton } from './button.tsx';
import { ButtonContent, goToNextOnboardingStep, Message, setOnboardingHouseholdSurveyResponse } from './common.tsx';
import { Frame } from './frame.tsx';

interface Props {
  onBack?(): void;
}

interface Option {
  prompt: string;
  householdSchedule: HouseholdSchedule;
}

export const SurveyHouseholdSchedule = ({ onBack }: Props) => {
  const { household } = useHousehold();
  const { schedule } = useLocalStorage(LocalStorageKey.OnboardingHouseholdSurveyResponses)[0];
  const [householdSchedule, setHouseholdSchedule] = useState<HouseholdSchedule[]>(
    () => household?.surveyResponses?.schedule ?? [],
  );
  // "Not sure yet" lets the customer move on without choosing a schedule (recorded as an empty schedule)
  const [notSureYet, setNotSureYet] = useState(false);
  const selectedOptions = schedule ?? householdSchedule;
  const navigate = useNavigate();

  const onBackWrapper = useCallback(() => {
    const householdToken = household?.token;
    if (!onBack || !householdToken) {
      console.error(`Expect onBack & household token`);
      return;
    }
    if (JSON.stringify(household?.surveyResponses?.schedule) !== JSON.stringify(householdSchedule)) {
      void (async () => {
        await server_api.recordHouseholdSurveyResponses({
          householdToken,
          responses: { ...household.surveyResponses, schedule: householdSchedule },
        });
        await synchronizeHouseholdWithServer({ force: true }); // initializes household.surveyResponses
      })();
    }
    onBack();
  }, [householdSchedule, household, onBack]);

  // force customer to choose an option during onboarding, but not on settings page
  const continueDisabled = (!selectedOptions || selectedOptions.length === 0) && !notSureYet && !onBack;

  const onContinue = useCallback(async () => {
    if (continueDisabled) {
      return;
    }
    if (onBack && household?.token) {
      return onBackWrapper();
    }
    await goToNextOnboardingStep(navigate, { from: OnboardingPages.SurveyHouseholdSchedule, action: 'continue' });
  }, [continueDisabled, household, onBack, onBackWrapper, navigate]);

  const options: Option[] = [
    { prompt: 'First thing in the morning', householdSchedule: HouseholdSchedule.Morning },
    { prompt: 'During our lunch time', householdSchedule: HouseholdSchedule.Lunch },
    { prompt: 'Around the dinner table', householdSchedule: HouseholdSchedule.Dinner },
    { prompt: 'Just before bedtime', householdSchedule: HouseholdSchedule.Bedtime },
  ];

  const onChooseOption = useCallback(
    (householdSchedule: HouseholdSchedule) => {
      return async () => {
        // choosing a schedule expresses certainty, which clears "Not sure yet"
        setNotSureYet(false);
        const isSelected = selectedOptions && selectedOptions.includes(householdSchedule);
        const newOptions = isSelected
          ? selectedOptions.filter((option) => option !== householdSchedule)
          : [...(selectedOptions ?? []), householdSchedule];
        if (onBack && household?.token) {
          setHouseholdSchedule(newOptions);
        } else {
          setOnboardingHouseholdSurveyResponse({ schedule: newOptions });
        }
      };
    },
    [selectedOptions, onBack, household?.token],
  );

  const onChooseNotSureYet = useCallback(async () => {
    setNotSureYet((notSureYet) => !notSureYet);
    setOnboardingHouseholdSurveyResponse({ schedule: [] });
  }, []);

  return (
    <Frame
      onboardingPage={OnboardingPages.SurveyHouseholdSchedule}
      mainContent={
        <>
          <Message $widthInChars={20}>
            When would you like to be reminded to sing?
            <br />
            (select all that apply)
          </Message>
          <ButtonContent>
            {options.map((option) => (
              <LeftAlignedButton
                key={option.householdSchedule}
                onClick={onChooseOption(option.householdSchedule)}
                notPrimary={true}
                selected={selectedOptions?.includes(option.householdSchedule)}
              >
                <OptionWrapper>{option.prompt}</OptionWrapper>
              </LeftAlignedButton>
            ))}
            {!onBack && (
              <LeftAlignedButton
                key="not-sure-yet"
                onClick={onChooseNotSureYet}
                notPrimary={true}
                selected={notSureYet}
              >
                <OptionWrapper>Not sure yet</OptionWrapper>
              </LeftAlignedButton>
            )}
          </ButtonContent>
        </>
      }
      footerContent={
        <Button onClick={onContinue} disabled={continueDisabled}>
          CONTINUE
        </Button>
      }
      onEnterKeypress={onContinue}
      isSmallBackButton={Boolean(onBack)}
      onBack={onBack && onBackWrapper}
    />
  );
};

const OptionWrapper = styled.div`
  padding: 10px 25px;
`;
